import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircleHeart, Waves } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  parent_name: "",
  phone: "",
  email: "",
  child_name: "",
  child_age: "",
  interest: "free_trial",
  unit_number: "",
  message: "",
};

const REQUIRED = ["parent_name", "unit_number", "phone", "email", "child_name", "child_age"];
const EMAIL_RE = /^\S+@\S+\.\S+$/;

const baseInputCls =
  "w-full rounded-xl border bg-white px-4 py-3 text-sm sm:text-base text-[#00334a] placeholder-[#00334a]/35 outline-none focus:border-[#c97049] focus:ring-2 focus:ring-[#c97049]/25 transition";

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-sm font-semibold text-[#00334a] mb-1.5">
    {children}
  </label>
);

export default function CallbackForm() {
  const [form, setForm] = useState(emptyForm);
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setMissing((m) => m.filter((k) => k !== key));
  };

  const fieldCls = (key) =>
    `${baseInputCls} ${
      missing.includes(key)
        ? "border-red-400 ring-2 ring-red-300/60"
        : "border-[#00334a]/20"
    }`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const bad = REQUIRED.filter((k) => !form[k].trim());
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) bad.push("email");
    if (bad.length) {
      setMissing(bad);
      toast.error("Please complete the highlighted fields.");
      const el = document.querySelector(`[data-testid="input-${bad[0].replace(/_/g, "-")}"]`);
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -160 });
        else el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus({ preventScroll: true });
      }
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      setSubmitted(data);
      toast.success("Thanks! Coach Abrie will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong sending your details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="mt-12 overflow-hidden rounded-[2rem] border border-[#00334a]/10 bg-white shadow-xl shadow-[#00334a]/10"
    >
      <div className="bg-[#f2ebd9] px-6 sm:px-10 py-7">
        <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#c97049]">
          <MessageCircleHeart className="h-4 w-4" />
          Rather hear from a human first?
        </p>
        <h3 className="mt-2 font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-[#00334a]">
          Coach Abrie will call or text you personally
        </h3>
        <p className="mt-2 text-sm sm:text-base text-[#00334a]/70 leading-relaxed">
          Leave your details and Coach Abrie, our Program Director, will reach out to plan
          your free trial or talk through the mini session.
        </p>
      </div>

      {submitted ? (
        <div data-testid="booking-success" className="px-6 sm:px-10 py-12 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-[#87856c]" />
          <h4 className="mt-5 font-serif text-2xl sm:text-3xl font-bold text-[#00334a]">
            Thanks, {submitted.parent_name.split(" ")[0]}!
          </h4>
          <p className="mx-auto mt-4 max-w-md text-sm sm:text-base leading-relaxed text-[#00334a]/70">
            We've passed your details to Coach Abrie, our Program Director — she'll text or
            email you shortly about{" "}
            <span className="font-semibold text-[#00334a]">{submitted.child_name}</span>'s{" "}
            {submitted.interest === "free_trial" ? "free trial lesson" : "spot in the 6-week mini session"}.
          </p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#f2ebd9] px-5 py-2.5 text-xs sm:text-sm font-medium text-[#00334a]">
            <Waves className="h-4 w-4 text-[#c97049]" />
            Bring a swimsuit, a towel, and your biggest smile
          </p>
        </div>
      ) : (
        <form data-testid="booking-form" onSubmit={handleSubmit} noValidate className="px-6 sm:px-10 py-8 space-y-6">
          <div data-testid="interest-selector" className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="What are you interested in?">
            {[
              { value: "free_trial", title: "Free Trial Lesson", sub: "Meet Coach Abrie + free lesson", id: "interest-free-trial" },
              { value: "mini_session", title: "6-Week Mini Session", sub: "15% resident discount", id: "interest-mini-session" },
            ].map((opt) => (
              <label
                key={opt.value}
                data-testid={opt.id}
                className={`cursor-pointer rounded-2xl border-2 px-5 py-4 transition-all duration-300 ${
                  form.interest === opt.value
                    ? "border-[#c97049] bg-[#e59a6d]/10 shadow-sm"
                    : "border-[#00334a]/15 bg-white hover:border-[#00334a]/35"
                }`}
              >
                <input
                  type="radio"
                  name="interest"
                  value={opt.value}
                  checked={form.interest === opt.value}
                  onChange={() => setForm((f) => ({ ...f, interest: opt.value }))}
                  className="sr-only"
                />
                <span className="block text-sm sm:text-base font-bold text-[#00334a]">{opt.title}</span>
                <span className="mt-0.5 block text-xs sm:text-sm text-[#52656d]">{opt.sub}</span>
              </label>
            ))}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="cb-parent-name">Parent's full name</Label>
              <input id="cb-parent-name" data-testid="input-parent-name" minLength={2}
                value={form.parent_name} onChange={set("parent_name")} placeholder="Jordan Miller" className={fieldCls("parent_name")} />
            </div>
            <div>
              <Label htmlFor="cb-unit-number">Apartment unit #</Label>
              <input id="cb-unit-number" data-testid="input-unit-number"
                value={form.unit_number} onChange={set("unit_number")} placeholder="e.g. 204" className={fieldCls("unit_number")} />
            </div>
            <div>
              <Label htmlFor="cb-phone">Phone</Label>
              <input id="cb-phone" data-testid="input-phone" type="tel" minLength={7}
                value={form.phone} onChange={set("phone")} placeholder="(515) 555-0123" className={fieldCls("phone")} />
            </div>
            <div>
              <Label htmlFor="cb-email">Email</Label>
              <input id="cb-email" data-testid="input-email" type="email"
                value={form.email} onChange={set("email")} placeholder="you@email.com" className={fieldCls("email")} />
            </div>
            <div>
              <Label htmlFor="cb-child-name">Child's name</Label>
              <input id="cb-child-name" data-testid="input-child-name"
                value={form.child_name} onChange={set("child_name")} placeholder="Ava" className={fieldCls("child_name")} />
            </div>
            <div>
              <Label htmlFor="cb-child-age">Child's age</Label>
              <select id="cb-child-age" data-testid="input-child-age"
                value={form.child_age} onChange={set("child_age")} className={fieldCls("child_age")}>
                <option value="" disabled>Select age range</option>
                <option value="1-3">1 – 3 years</option>
                <option value="4-6">4 – 6 years</option>
                <option value="7-9">7 – 9 years</option>
                <option value="10-12">10 – 12 years</option>
              </select>
            </div>
          </div>

          {missing.length > 0 && (
            <p data-testid="form-missing-warning" className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm font-semibold text-red-600">
              Please complete the highlighted fields above, then hit the button again.
            </p>
          )}

          <div>
            <Label htmlFor="cb-message">Anything we should know? (optional)</Label>
            <textarea id="cb-message" data-testid="input-message" rows={3}
              value={form.message} onChange={set("message")}
              placeholder="Nervous swimmer? Previous lessons? Tell us here." className={`${fieldCls("message")} resize-none`} />
          </div>

          <button
            type="submit"
            data-testid="booking-form-submit-button"
            disabled={submitting}
            className="w-full rounded-xl bg-[#c97049] py-4 text-base sm:text-lg font-bold text-white shadow-lg shadow-[#c97049]/30 hover:bg-[#b25f3a] disabled:opacity-60 transition-all duration-300 inline-flex items-center justify-center gap-2.5"
          >
            {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
            {submitting ? "Sending…" : "Ask Coach Abrie to Reach Out"}
          </button>
          <p className="text-center text-xs text-[#52656d]">
            We reply personally to every request · No payment due today
          </p>
        </form>
      )}
    </motion.div>
  );
}
